import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '@/lib/api'
import { Printer, Loader2, Play, FileText } from 'lucide-react'
import FundIdentityCard from '@/components/ui/FundIdentityCard'

type Fund = { id: string; name: string; ticker: string; source: string }

type StyleBucket = { bucket: string; share: number; n_factors: number }

type FactorResp = {
  run_id: number; n_obs: number
  r_squared: number | null; alpha_weekly: number | null; alpha_annualized: number | null
  factors: { factor: string; beta: number | null; beta_ortho: number | null;
             t_stat: number | null; p_value: number | null; significant: boolean;
             correlation: number | null }[]
  style_breakdown?: StyleBucket[]
}

type VaRResp = {
  run_id: number; n_obs: number
  results: { model: string; horizon: number; confidence: number; var: number | null; es: number | null; method?: string }[]
}

type StressResp = {
  run_id: number; n_obs: number
  results: { model: string; scenario: string; mean: number | null;
             var_95: number | null; es_95: number | null; method?: string; status?: string }[]
}

type EventResp = {
  run_id: number; n_obs: number
  results: {
    event: string; name: string; driver_dd: number | null
    fund_ret: number | null; predicted_ret: number | null
    residual: number | null; beta_to_driver: number | null
  }[]
}

type BacktestResp = {
  run_id: number
  horizon: number; confidence: number
  n_obs: number; n_exceedances: number; expected_exceedances: number | null
  exceedance_rate: number | null; kupiec_p_value: number | null
  ytd_max_drawdown: number | null
  regime_stats?: { regime: string; n_obs: number; n_exceedances: number; kupiec_p_value: number | null }[]
}

type AlphaResp = {
  run_id: number
  peer_ticker: string; n_obs: number
  beta: number | null; alpha_annualized: number | null
  t_alpha: number | null; p_alpha: number | null
  alpha_significant: boolean
}

type NarrativeResp = {
  fund_id: string; fund_name: string; ticker: string; mandate: string
  paragraphs: { title: string; text: string; run_id?: number }[]
  synthesis: string
  source_runs: Record<string, number | null>
}

export default function RiskReportView() {
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState('')
  const [universe, setUniverse] = useState('narrow')
  const [timeWindow, setTimeWindow] = useState('full')

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })

  const factorM = useMutation<FactorResp, unknown>({
    mutationFn: () => api.post('/risk-stats/factor-model', {
      fund_id: fundId, universe, time_window: timeWindow, top_k: 15,
    }).then(r => r.data),
  })
  const varM = useMutation<VaRResp, unknown>({
    mutationFn: () => api.post('/risk-stats/var', {
      fund_id: fundId,
      models: ['gaussian', 'historical', 'polymodel_narrow'],
      horizons: [1, 5, 21], confidences: [0.95, 0.99],
      universe, time_window: timeWindow, n_samples: 5000,
    }).then(r => r.data),
  })
  const stressM = useMutation<StressResp, unknown>({
    mutationFn: () => api.post('/risk-stats/stress', {
      fund_id: fundId,
      models: ['vine_pca', 'vine_ortho', 'polymodel_narrow'],
      scenarios: ['MSCI World -10%', 'SPX -10%', 'Vol Spike', 'WTI -10%'],
      universe, time_window: timeWindow, n_samples: 5000,
    }).then(r => r.data),
  })
  const eventM = useMutation<EventResp, unknown>({
    mutationFn: () => api.post('/risk-stats/event-stress', {
      fund_id: fundId,
      event_ids: ['liberation_day_2025', 'iran_crisis_2026', 'rate_shock_2022', 'covid_crash_2020'],
      universe, time_window: timeWindow, method: 'multi_factor', top_k: 15,
    }).then(r => r.data),
  })
  const backM = useMutation<BacktestResp, unknown>({
    mutationFn: () => api.post('/risk-stats/backtest', {
      fund_id: fundId, horizon: 5, confidence: 0.99, window_days: 250,
      time_window: timeWindow,
    }).then(r => r.data),
  })
  const alphaM = useMutation<AlphaResp, unknown>({
    mutationFn: () => api.post('/risk-stats/alpha-vs-peer', {
      fund_id: fundId, peer_ticker: 'SPX Index', time_window: timeWindow,
    }).then(r => r.data),
  })
  const narrativeM = useMutation<NarrativeResp, unknown>({
    mutationFn: () => api.get(`/risk-stats/narrative/${encodeURIComponent(fundId)}`).then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data.filter(f =>
      !s || f.name?.toLowerCase().includes(s) || f.ticker?.toLowerCase().includes(s)
    ).slice(0, 300)
  }, [fundsQ.data, search])

  const selectedFund = fundsQ.data?.find(f => f.id === fundId)

  const runAll = async () => {
    // Launch compute runs first
    const results = await Promise.allSettled([
      factorM.mutateAsync(), varM.mutateAsync(), stressM.mutateAsync(),
      eventM.mutateAsync(), backM.mutateAsync(), alphaM.mutateAsync(),
    ])
    // Then narrative (reads the newly persisted runs)
    await narrativeM.mutateAsync().catch(() => {})
    return results
  }
  const anyPending = factorM.isPending || varM.isPending || stressM.isPending
    || eventM.isPending || backM.isPending || alphaM.isPending || narrativeM.isPending

  const pct = (v: number | null | undefined, d = 2) => v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const num = (v: number | null | undefined, d = 3) => v == null ? '—' : v.toFixed(d)
  const cls = (v: number | null | undefined) => v == null ? '' : v < 0 ? 'text-red-600' : 'text-green-600'

  // VaR matrix
  const varMatrix = useMemo(() => {
    const m = new Map<string, Record<string, { var: number | null; es: number | null }>>()
    for (const r of varM.data?.results ?? []) {
      const key = `${r.horizon}d_${Math.round(r.confidence * 100)}`
      if (!m.has(r.model)) m.set(r.model, {})
      m.get(r.model)![key] = { var: r.var, es: r.es }
    }
    return m
  }, [varM.data])

  const stressScenarios = useMemo(() => {
    const s = new Set<string>()
    for (const r of stressM.data?.results ?? []) s.add(r.scenario)
    return Array.from(s)
  }, [stressM.data])
  const stressModels = useMemo(() => {
    const s = new Set<string>()
    for (const r of stressM.data?.results ?? []) s.add(r.model)
    return Array.from(s)
  }, [stressM.data])
  const stressLookup = useMemo(() => {
    const m = new Map<string, { mean: number | null; var_95: number | null; es_95: number | null }>()
    for (const r of stressM.data?.results ?? []) m.set(`${r.scenario}|${r.model}`, r)
    return m
  }, [stressM.data])

  return (
    <div className="p-6 space-y-6 print:p-0 print:space-y-3">
      {/* Non-print controls */}
      <div className="print:hidden flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund risk report</h1>
          <p className="text-muted-foreground">
            One-page composite: factor model · VaR · stress · event replay · backtest · peer alpha · narrative.
            Designed for PDF export (⌘/Ctrl+P).
          </p>
        </div>
        <button
          disabled={!fundId || !factorM.data || anyPending}
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm hover:bg-muted disabled:opacity-40"
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="print:hidden bg-card border border-border rounded-lg p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input type="text" placeholder="Search fund..." value={search} onChange={e => setSearch(e.target.value)} className="input flex-1" />
          <select value={fundId} onChange={e => setFundId(e.target.value)} className="input w-full md:w-[28rem]">
            <option value="">-- Select fund --</option>
            {filteredFunds.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.ticker}) [{f.source}]</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Universe</span>
            <select value={universe} onChange={e => setUniverse(e.target.value)} className="input">
              <option value="narrow">narrow</option>
              <option value="wide">wide</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Window</span>
            <select value={timeWindow} onChange={e => setTimeWindow(e.target.value)} className="input">
              <option value="full">full</option>
              <option value="10y">10y</option>
              <option value="5y">5y</option>
              <option value="2y">2y</option>
            </select>
          </label>
          <button
            disabled={!fundId || anyPending}
            onClick={runAll}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
          >
            {anyPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run full report
          </button>
        </div>
        <div className="text-xs text-muted-foreground">
          {factorM.isPending && 'factor… '}
          {varM.isPending && 'VaR… '}
          {stressM.isPending && 'stress… '}
          {eventM.isPending && 'events… '}
          {backM.isPending && 'backtest… '}
          {alphaM.isPending && 'alpha… '}
          {narrativeM.isPending && 'narrative… '}
          {!anyPending && factorM.data && '✓ factor '}
          {!anyPending && varM.data && '✓ VaR '}
          {!anyPending && stressM.data && '✓ stress '}
          {!anyPending && eventM.data && '✓ events '}
          {!anyPending && backM.data && '✓ backtest '}
          {!anyPending && alphaM.data && '✓ alpha '}
          {!anyPending && narrativeM.data && '✓ narrative'}
        </div>
      </div>

      {/* Printable report body */}
      {fundId && selectedFund && (
        <div className="space-y-6 print:space-y-3">
          {/* Cover */}
          <div className="border-b border-border pb-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Plutus risk report</div>
            <div className="text-2xl font-semibold mt-1">{selectedFund.name}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {selectedFund.ticker} · universe={universe} · window={timeWindow} · as of {new Date().toLocaleDateString()}
              {narrativeM.data?.mandate && <span> · mandate <span className="font-mono">{narrativeM.data.mandate}</span></span>}
            </div>
          </div>

          {/* 0. Fund identity card */}
          <FundIdentityCard fundId={fundId} />

          {/* 1. Factor model + style */}
          <section>
            <h2 className="text-lg font-semibold mb-2">1 · Factor model and investment style</h2>
            {!factorM.data && <Placeholder text="Run full report to populate." />}
            {factorM.data && (
              <>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <Mini label="R² (weekly)"  value={num(factorM.data.r_squared)} />
                  <Mini label="α (ann.)"      value={pct(factorM.data.alpha_annualized)} />
                  <Mini label="# obs"         value={String(factorM.data.n_obs)} />
                  <Mini label="# sig (p<.05)" value={String(factorM.data.factors.filter(f => f.significant).length)} />
                </div>
                {(factorM.data.style_breakdown?.length ?? 0) > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-muted-foreground mb-1">Style breakdown (share of |β ortho|)</div>
                    {factorM.data.style_breakdown!.slice(0, 8).map(b => (
                      <div key={b.bucket} className="flex items-center gap-2 text-xs mb-0.5">
                        <div className="w-36">{b.bucket}</div>
                        <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                          <div className="h-2 bg-primary" style={{ width: `${Math.round(b.share * 100)}%` }} />
                        </div>
                        <div className="w-12 text-right font-mono">{(b.share * 100).toFixed(1)}%</div>
                      </div>
                    ))}
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 px-2">Factor</th>
                      <th className="text-right py-1.5 px-2">β</th>
                      <th className="text-right py-1.5 px-2">β ortho</th>
                      <th className="text-right py-1.5 px-2">t</th>
                      <th className="text-right py-1.5 px-2">p</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...factorM.data.factors].sort((a, b) => Math.abs(b.t_stat ?? 0) - Math.abs(a.t_stat ?? 0)).slice(0, 10).map(f => (
                      <tr key={f.factor} className={'border-b border-border/50 ' + (f.significant ? 'bg-primary/5' : '')}>
                        <td className="py-1.5 px-2 font-mono text-xs">{f.factor}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{num(f.beta)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{num(f.beta_ortho)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{num(f.t_stat, 2)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{num(f.p_value, 4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          {/* 2. Alpha vs SPX */}
          {alphaM.data && (
            <section>
              <h2 className="text-lg font-semibold mb-2">2 · Alpha vs SPX Index</h2>
              <div className="grid grid-cols-4 gap-3">
                <Mini label="β vs SPX"  value={num(alphaM.data.beta, 2)} />
                <Mini label="α (ann.)"   value={pct(alphaM.data.alpha_annualized)} />
                <Mini label="t-stat"     value={num(alphaM.data.t_alpha, 2)} />
                <Mini label="Significant" value={alphaM.data.alpha_significant ? `yes (p=${num(alphaM.data.p_alpha, 3)})` : `no (p=${num(alphaM.data.p_alpha, 3)})`} />
              </div>
            </section>
          )}

          {/* 3. VaR matrix */}
          <section>
            <h2 className="text-lg font-semibold mb-2">3 · Value at Risk (weekly overlapping)</h2>
            {!varM.data && <Placeholder text="Run full report to populate." />}
            {varM.data && varMatrix.size > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2">Model</th>
                    {[1, 5, 21].flatMap(h => [95, 99].map(c => (
                      <th key={`${h}-${c}`} className="text-right py-1.5 px-2 text-xs">
                        <div className="text-muted-foreground">{h}d · {c}%</div>
                      </th>
                    )))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(varMatrix.entries()).map(([model, cells]) => (
                    <tr key={model} className="border-b border-border/50">
                      <td className="py-1.5 px-2 font-mono text-xs">{model}</td>
                      {[1, 5, 21].flatMap(h => [95, 99].map(c => {
                        const cell = cells[`${h}d_${c}`]
                        return (
                          <td key={`${h}-${c}`} className="py-1.5 px-2 text-right font-mono text-xs">
                            {pct(cell?.var)}
                          </td>
                        )
                      }))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* 4. Stress matrix */}
          <section>
            <h2 className="text-lg font-semibold mb-2">4 · Stress test matrix</h2>
            {!stressM.data && <Placeholder text="Run full report to populate." />}
            {stressM.data && stressScenarios.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2">Scenario</th>
                    {stressModels.map(m => <th key={m} className="text-right py-1.5 px-2 font-mono text-xs">{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {stressScenarios.map(s => (
                    <tr key={s} className="border-b border-border/50">
                      <td className="py-1.5 px-2 text-xs">{s}</td>
                      {stressModels.map(m => {
                        const r = stressLookup.get(`${s}|${m}`)
                        return (
                          <td key={m} className={'py-1.5 px-2 text-right font-mono text-xs ' + cls(r?.mean)}>
                            {pct(r?.mean)}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* 5. Event replays */}
          <section>
            <h2 className="text-lg font-semibold mb-2">5 · Historical event replays (multi-factor)</h2>
            {!eventM.data && <Placeholder text="Run full report to populate." />}
            {eventM.data && eventM.data.results.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1.5 px-2">Event</th>
                    <th className="text-right py-1.5 px-2">Driver DD</th>
                    <th className="text-right py-1.5 px-2">Predicted</th>
                    <th className="text-right py-1.5 px-2">Realised</th>
                    <th className="text-right py-1.5 px-2">Residual</th>
                  </tr>
                </thead>
                <tbody>
                  {eventM.data.results.map(r => (
                    <tr key={r.event} className="border-b border-border/50">
                      <td className="py-1.5 px-2 text-xs">{r.name}</td>
                      <td className={'py-1.5 px-2 text-right font-mono text-xs ' + cls(r.driver_dd)}>{pct(r.driver_dd)}</td>
                      <td className={'py-1.5 px-2 text-right font-mono text-xs ' + cls(r.predicted_ret)}>{pct(r.predicted_ret)}</td>
                      <td className={'py-1.5 px-2 text-right font-mono text-xs ' + cls(r.fund_ret)}>{pct(r.fund_ret)}</td>
                      <td className={'py-1.5 px-2 text-right font-mono text-xs ' + cls(r.residual)}>{pct(r.residual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* 6. Backtest */}
          {backM.data && (
            <section>
              <h2 className="text-lg font-semibold mb-2">6 · VaR backtest (5-day · 99%)</h2>
              <div className="grid grid-cols-5 gap-3">
                <Mini label="n obs"            value={String(backM.data.n_obs)} />
                <Mini label="Exc / expected"   value={`${backM.data.n_exceedances} / ${Math.round(backM.data.expected_exceedances ?? 0)}`} />
                <Mini label="Rate"             value={backM.data.exceedance_rate == null ? '—' : `${(backM.data.exceedance_rate * 100).toFixed(2)}%`} />
                <Mini label="Kupiec p"         value={num(backM.data.kupiec_p_value, 3)} />
                <Mini label="YTD max DD"       value={pct(backM.data.ytd_max_drawdown)} />
              </div>
              {(backM.data.regime_stats?.length ?? 0) > 0 && (
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 px-2">Regime</th>
                      <th className="text-right py-1.5 px-2">n</th>
                      <th className="text-right py-1.5 px-2">Exceedances</th>
                      <th className="text-right py-1.5 px-2">Kupiec p</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backM.data.regime_stats!.map(r => (
                      <tr key={r.regime} className="border-b border-border/50">
                        <td className="py-1.5 px-2 font-mono text-xs">{r.regime}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{r.n_obs}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-xs">{r.n_exceedances}</td>
                        <td className={'py-1.5 px-2 text-right font-mono text-xs ' + (r.kupiec_p_value != null && r.kupiec_p_value < 0.05 ? 'text-red-600' : 'text-green-600')}>
                          {num(r.kupiec_p_value, 3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {/* 7. Narrative */}
          {narrativeM.data && (
            <section>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <FileText className="h-5 w-5" /> 7 · Synthesis
              </h2>
              <div className="space-y-3">
                {narrativeM.data.paragraphs.map((p, i) => (
                  <div key={i} className="border border-border rounded p-3">
                    <div className="text-sm font-medium mb-1">{p.title}</div>
                    <div className="text-sm text-muted-foreground">{p.text}</div>
                  </div>
                ))}
                <div className="text-xs text-muted-foreground italic">{narrativeM.data.synthesis}</div>
              </div>
            </section>
          )}

          <div className="text-xs text-muted-foreground pt-4 border-t border-border">
            Source runs — Factor {factorM.data?.run_id ?? '—'} · VaR {varM.data?.run_id ?? '—'} · Stress {stressM.data?.run_id ?? '—'} · Events {eventM.data?.run_id ?? '—'} · Backtest {backM.data?.run_id ?? '—'} · α vs SPX {alphaM.data?.run_id ?? '—'}.
          </div>
        </div>
      )}
    </div>
  )
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border rounded p-2">
      <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className="text-sm font-mono mt-0.5">{value}</div>
    </div>
  )
}
function Placeholder({ text }: { text: string }) {
  return <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-3">{text}</div>
}
